import React, { useState, useEffect } from 'react';
import { Row, Col, Card, Table, Badge, Button, Form, InputGroup, Alert, Modal } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { FaSearch, FaEye, FaSort, FaSortUp, FaSortDown, FaFileInvoice, FaBoxOpen } from 'react-icons/fa';
import { getAdminOrders, updateAdminOrderStatus } from '../../services/adminService';

const AdminOrders = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Filters and search
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [paymentStatusFilter, setPaymentStatusFilter] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: 'dateCreated', direction: 'desc' });
  
  // Modal states
  const [showOrderModal, setShowOrderModal] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  
  // Fetch orders data
  useEffect(() => {
    let mounted = true;

    const fetchOrders = async () => {
      setLoading(true);
      setError('');
      try {
        const data = await getAdminOrders();
        if (!mounted) return;
        setOrders(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error(err);
        if (!mounted) return;
        setError(err?.message || 'Failed to load orders');
        setOrders([]);
      } finally {
        if (mounted) setLoading(false);
      }
    };

    fetchOrders();
    return () => {
      mounted = false;
    };
  }, []);

  const getNestedValue = (obj, key) => {
    if (!obj || !key) return undefined;
    return key.split('.').reduce((acc, part) => (acc && acc[part] !== undefined ? acc[part] : undefined), obj);
  };
  
  // Handle sort
  const requestSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  // Get sort icon
  const getSortIcon = (key) => {
    if (sortConfig.key !== key) {
      return <FaSort />;
    }
    if (sortConfig.direction === 'asc') {
      return <FaSortUp />;
    }
    return <FaSortDown />;
  };

  // Filter and sort orders
  const processedOrders = React.useMemo(() => {
    let filteredOrders = [...orders];

    // Search
    if (searchTerm) {
      filteredOrders = filteredOrders.filter(order => 
        order.id.toLowerCase().includes(searchTerm.toLowerCase()) ||
        order.buyer.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        order.buyer.email.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Filter by status
    if (statusFilter) {
      filteredOrders = filteredOrders.filter(order => order.status === statusFilter);
    }

    // Filter by payment status
    if (paymentStatusFilter) {
      filteredOrders = filteredOrders.filter(order => order.paymentStatus === paymentStatusFilter);
    }

    // Sort
    if (sortConfig.key) {
      filteredOrders.sort((a, b) => {
        const aVal = getNestedValue(a, sortConfig.key);
        const bVal = getNestedValue(b, sortConfig.key);
        if (aVal < bVal) {
          return sortConfig.direction === 'asc' ? -1 : 1;
        }
        if (aVal > bVal) {
          return sortConfig.direction === 'asc' ? 1 : -1;
        }
        return 0;
      });
    }

    return filteredOrders;
  }, [orders, searchTerm, statusFilter, paymentStatusFilter, sortConfig]);

  // Handle order status update
  const handleUpdateOrderStatus = async (orderId, newStatus) => {
    try {
      setError('');
      const updated = await updateAdminOrderStatus(orderId, newStatus);
      setOrders(prevOrders => prevOrders.map(o => (String(o.id) === String(orderId) ? updated : o)));
      setSuccess(`Order #${orderId} status updated to ${newStatus}.`);
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error(err);
      setError(err?.message || 'Failed to update order status');
      setTimeout(() => setError(''), 3000);
    }
  };

  // Open order modal
  const handleOpenOrderModal = (order) => {
    setSelectedOrder(order);
    setShowOrderModal(true);
  };
  
  return (
    <>
      <div className="admin-page-content">
      <h2 className="mb-4">Order Management</h2>
        
        {error && <Alert variant="danger">{error}</Alert>}
        {success && <Alert variant="success">{success}</Alert>}

        <Card className="mb-4">
          <Card.Body>
            <Row>
              <Col md={4}>
                <Form.Group>
                  <Form.Label>Search</Form.Label>
                  <InputGroup>
                    <Form.Control 
                      type="text"
                      placeholder="Search by ID, name, email..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                    <InputGroup.Text><FaSearch /></InputGroup.Text>
                  </InputGroup>
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group>
                  <Form.Label>Order Status</Form.Label>
                  <Form.Select 
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                  >
                    <option value="">All Statuses</option>
                    <option value="pending">Pending</option>
                    <option value="processing">Processing</option>
                    <option value="completed">Completed</option>
                    <option value="cancelled">Cancelled</option>
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group>
                  <Form.Label>Payment Status</Form.Label>
                  <Form.Select
                    value={paymentStatusFilter}
                    onChange={(e) => setPaymentStatusFilter(e.target.value)}
                  >
                    <option value="">All Payments</option>
                    <option value="pending">Pending</option>
                    <option value="paid">Paid</option>
                    <option value="refunded">Refunded</option>
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        <Card>
          <Card.Header>
            <FaBoxOpen className="me-2" /> Orders List
          </Card.Header>
          <Card.Body>
            {loading ? (
              <div className="text-center">Loading orders...</div>
            ) : (
              <Table responsive hover className="align-middle">
                <thead>
                  <tr>
                    <th onClick={() => requestSort('id')}>ID {getSortIcon('id')}</th>
                    <th onClick={() => requestSort('buyer.name')}>Buyer {getSortIcon('buyer.name')}</th>
                    <th onClick={() => requestSort('total')}>Total {getSortIcon('total')}</th>
                    <th onClick={() => requestSort('status')}>Status {getSortIcon('status')}</th>
                    <th onClick={() => requestSort('paymentStatus')}>Payment {getSortIcon('paymentStatus')}</th>
                    <th onClick={() => requestSort('dateCreated')}>Date Created {getSortIcon('dateCreated')}</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {processedOrders.map(order => (
                    <tr key={order.id}>
                      <td>{order.id}</td>
                      <td>{order.buyer.name}</td>
                      <td>GH₵ {order.total.toFixed(2)}</td>
                      <td>
                        <Badge 
                          bg={{
                            pending: 'warning',
                            processing: 'primary',
                            completed: 'success',
                            cancelled: 'danger'
                          }[order.status]}
                        >
                          {order.status}
                        </Badge>
                      </td>
                      <td>
                        <Badge 
                          bg={{
                            pending: 'secondary',
                            paid: 'success',
                            refunded: 'info'
                          }[order.paymentStatus]}
                        >
                          {order.paymentStatus}
                        </Badge>
                      </td>
                      <td>{new Date(order.dateCreated).toLocaleDateString()}</td>
                      <td>
                        <Button 
                          variant="outline-primary"
                          size="sm"
                          onClick={() => handleOpenOrderModal(order)}
                        >
                          <FaEye />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
          </Card.Body>
        </Card>

        {/* Order Details Modal */}
        <Modal show={showOrderModal} onHide={() => setShowOrderModal(false)} size="lg">
          <Modal.Header closeButton>
            <Modal.Title>Order Details #{selectedOrder?.id}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {selectedOrder && (
              <>
                <Row className="mb-4">
                  <Col md={6}>
                    <h5>Order Information</h5>
                    <p className="mb-1"><strong>Status:</strong> 
                      <Badge 
                        bg={{
                          pending: 'warning',
                          processing: 'primary',
                          completed: 'success',
                          cancelled: 'danger'
                        }[selectedOrder.status]}
                      >
                        {selectedOrder.status}
                      </Badge>
                    </p>
                    <p className="mb-1"><strong>Payment Method:</strong> {selectedOrder.paymentMethod}</p>
                    <p className="mb-1"><strong>Payment Status:</strong> 
                      <Badge 
                        bg={{
                          pending: 'secondary',
                          paid: 'success',
                          refunded: 'info'
                        }[selectedOrder.paymentStatus]}
                      >
                        {selectedOrder.paymentStatus}
                      </Badge>
                    </p>
                    <p className="mb-1"><strong>Date Created:</strong> {new Date(selectedOrder.dateCreated).toLocaleString()}</p>
                    <p className="mb-1"><strong>Last Updated:</strong> {new Date(selectedOrder.dateUpdated).toLocaleString()}</p>
                  </Col>
                  <Col md={6}>
                    <h5>Buyer Information</h5>
                    <p className="mb-1"><strong>Name:</strong> {selectedOrder.buyer.name}</p>
                    <p className="mb-1"><strong>Email:</strong> {selectedOrder.buyer.email}</p>
                    <p className="mb-1"><strong>Delivery Address:</strong> {selectedOrder.deliveryAddress}</p>
                    <Button 
                      as={Link}
                      to={`/admin/users/${selectedOrder.buyer.id}`}
                      variant="outline-primary"
                      size="sm"
                    >
                      View Buyer Profile
                    </Button>
                  </Col>
                </Row>
                
                <h5>Order Items</h5>
                <Table bordered hover className="mb-4">
                  <thead>
                    <tr>
                      <th>Product</th>
                      <th>Price</th>
                      <th>Quantity</th>
                      <th>Subtotal</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedOrder.items.map(item => (
                      <tr key={item.id}>
                        <td>{item.name}</td>
                        <td>GH₵ {item.price.toFixed(2)}</td>
                        <td>{item.quantity}</td>
                        <td>GH₵ {(item.price * item.quantity).toFixed(2)}</td>
                      </tr>
                    ))}
                    <tr className="fw-bold">
                      <td colSpan={3} className="text-end">Total:</td>
                      <td>GH₵ {selectedOrder.total.toFixed(2)}</td>
                    </tr>
                  </tbody>
                </Table>
                
                <h5>Update Order Status</h5>
                <div className="d-flex gap-2 mb-3">
                  <Button 
                    variant="warning"
                    size="sm"
                    onClick={() => {
                      handleUpdateOrderStatus(selectedOrder.id, 'pending');
                      setShowOrderModal(false);
                    }}
                    disabled={selectedOrder.status === 'pending'}
                  >
                    Mark as Pending
                  </Button>
                  <Button 
                    variant="primary"
                    size="sm"
                    onClick={() => {
                      handleUpdateOrderStatus(selectedOrder.id, 'processing');
                      setShowOrderModal(false);
                    }}
                    disabled={selectedOrder.status === 'processing'}
                  >
                    Mark as Processing
                  </Button>
                  <Button 
                    variant="success"
                    size="sm"
                    onClick={() => {
                      handleUpdateOrderStatus(selectedOrder.id, 'completed');
                      setShowOrderModal(false);
                    }}
                    disabled={selectedOrder.status === 'completed'}
                  >
                    Mark as Completed
                  </Button>
                  <Button 
                    variant="danger"
                    size="sm"
                    onClick={() => {
                      handleUpdateOrderStatus(selectedOrder.id, 'cancelled');
                      setShowOrderModal(false);
                    }}
                    disabled={selectedOrder.status === 'cancelled'}
                  >
                    Mark as Cancelled
                  </Button>
                </div>
              </>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowOrderModal(false)}>
              Close
            </Button>
            <Button 
              variant="primary"
              as={Link}
              to={`/admin/orders/${selectedOrder?.id}/invoice`}
            >
              View Invoice
            </Button>
          </Modal.Footer>
        </Modal>
      </div>
    </>
  );
};

export default AdminOrders;